import { Progress } from "@/components/ui/progress";
import { Check, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

export function WizardHeader({ step, total, title, subtitle }) {
  const { t } = useTranslation();
  const pct = Math.round(((step + 1) / total) * 100);
  // Motivational banner shown from step index 3 (step 4 of wizard) onwards, before the final gate
  const showEncourage = step >= 3 && step < total - 1;
  return (
    <div className="border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-20">
      <div className="mx-auto max-w-4xl px-6 py-5">
        <div className="flex items-center justify-between text-xs uppercase tracking-[0.14em] font-semibold text-slate-500">
          <span data-testid="wizard-step-indicator">{t("wizard.step_of", { current: step + 1, total })}</span>
          <span>{pct} %</span>
        </div>
        <Progress value={pct} className="mt-3 h-1.5 bg-slate-100" />
        <div className="mt-5">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" data-testid="wizard-step-title">{title}</h2>
          {subtitle && <p className="mt-1 text-slate-500 leading-relaxed">{subtitle}</p>}
        </div>
        {showEncourage && (
          <div className="mt-4 flex items-start gap-3 rounded-sm border border-fuchsia-200 bg-fuchsia-50/50 px-4 py-3" data-testid="wizard-encouragement">
            <Sparkles className="h-4 w-4 text-brand-fuchsia mt-0.5 shrink-0" />
            <div className="text-sm">
              <div className="font-semibold text-brand-bordeaux">{t("wizard.encourage_title")}</div>
              <div className="text-slate-600 leading-relaxed">{t("wizard.encourage_body")}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function OptionCard({ selected, onClick, children, testId, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      data-selected={selected}
      className={`text-left w-full rounded-sm border p-4 md:p-5 transition-colors bg-white flex items-start gap-3
        ${selected ? "border-brand-fuchsia ring-1 ring-brand-fuchsia bg-fuchsia-50/40" : "border-slate-200 hover:border-brand-fuchsia"}
        ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      <div className={`mt-0.5 h-4 w-4 rounded-full border ${selected ? "bg-brand-fuchsia border-brand-fuchsia" : "border-slate-300"} flex items-center justify-center`}>
        {selected && <Check className="h-3 w-3 text-white" />}
      </div>
      <div className="flex-1 text-slate-800">{children}</div>
    </button>
  );
}

export function WizardFooter({ onBack, onNext, canNext, nextLabel, backLabel, loading }) {
  const { t } = useTranslation();
  const backTxt = backLabel || t("wizard.back");
  const nextTxt = nextLabel || t("wizard.next");
  return (
    <div className="border-t border-slate-200 bg-white sticky bottom-0 z-20">
      <div className="mx-auto max-w-4xl px-6 py-4 flex items-center justify-between">
        <button
          onClick={onBack}
          className="btn-ghost py-2 px-4 text-sm"
          data-testid="wizard-back"
          disabled={loading}
        >
          <ChevronLeft className="h-4 w-4" /> {backTxt}
        </button>
        <button
          onClick={onNext}
          className="btn-primary py-2 px-4 text-sm"
          disabled={!canNext || loading}
          data-testid="wizard-next"
        >
          {loading ? t("wizard.processing") : nextTxt} <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
